import { useEffect, useImperativeHandle, useMemo, useState } from "react";
import type { ComponentProps, Ref } from "react";

import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { MeasurementOverlay } from "@/viewport-tools/measurement/overlay/MeasurementOverlay";

export interface FieldMeasurementOverlayHandle {
  previewStrokeWidth: (
    ids: Id<"takeoffPilotMeasurements">[], width: number | null
  ) => ((saved: boolean) => void) | undefined;
}

// Keep transient width edits inside the overlay instead of rerendering the job page.
export function FieldMeasurementOverlay({
  ref,
  measurements,
  ...props
}: ComponentProps<typeof MeasurementOverlay> & { ref?: Ref<FieldMeasurementOverlayHandle> }) {
  const [widths, setWidths] = useState<Map<string, { width: number; saved: boolean }>>(() => new Map());

  useImperativeHandle(ref, () => ({
    previewStrokeWidth(ids, width) {
      const preview = width === null ? null : { width, saved: false };
      setWidths((current) => {
        const next = new Map(current);
        for (const id of ids) {
          if (preview === null) next.delete(String(id));
          else next.set(String(id), preview);
        }
        return next;
      });
      if (preview === null) return;
      // Identity ties completion to this exact edit, even when a newer draft has the same width.
      return (saved: boolean) => setWidths((current) => {
        const next = new Map(current);
        for (const id of ids) {
          if (next.get(String(id)) !== preview) continue;
          if (saved) next.set(String(id), { ...preview, saved: true });
          else next.delete(String(id));
        }
        return next;
      });
    },
  }), []);

  useEffect(() => {
    // Hand back to persisted data only once it contains the previewed width.
    setWidths((current) => {
      const next = new Map(current);
      for (const measurement of measurements) {
        const preview = next.get(String(measurement._id));
        if (preview?.saved && preview.width === (measurement.annotation?.strokeWidth ?? 2)) {
          next.delete(String(measurement._id));
        }
      }
      return next.size === current.size ? current : next;
    });
  }, [measurements, widths]);

  const previewMeasurements = useMemo(() => measurements.map((measurement) => {
    const strokeWidth = widths.get(String(measurement._id))?.width;
    return strokeWidth === undefined ? measurement : {
      ...measurement,
      annotation: { ...measurement.annotation, strokeWidth },
    };
  }), [measurements, widths]);

  return <MeasurementOverlay {...props} measurements={previewMeasurements} />;
}
